import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Upload, X, CheckCircle2, Loader2 } from "lucide-react";
import { contact, customerTypes, serviceOptions } from "@/config/content";
import { Reveal } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "", company: "", phone: "", email: "", postal_code: "", city: "",
  customer_type: customerTypes[0], service: "", object_type: "", project_size: "",
  preferred_start: "", message: "", consent: false, website: "",
};

const Field = ({ label, required, children, error, id }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-sm font-medium text-charcoal">
      {label} {required && <span className="text-forest">*</span>}
    </label>
    {children}
    {error && <p id={`${id}-error`} className="text-sm text-destructive" role="alert">{error}</p>}
  </div>
);

const inputCls = "w-full rounded-md border border-input bg-white px-3.5 py-2.5 text-charcoal placeholder:text-charcoal-muted/60 focus:outline-none focus:ring-2 focus:ring-forest/40 focus:border-forest transition-colors";

export const ContactForm = () => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, service: e.detail || f.service }));
    window.addEventListener("preselect-service", handler);
    return () => window.removeEventListener("preselect-service", handler);
  }, []);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Bitte geben Sie Ihren Namen an.";
    if (!form.phone.trim()) e.phone = "Bitte geben Sie eine Telefonnummer an.";
    if (!form.email.trim()) e.email = "Bitte geben Sie Ihre E-Mail-Adresse an.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Bitte geben Sie eine gültige E-Mail-Adresse an.";
    if (!form.postal_code.trim()) e.postal_code = "Bitte geben Sie Ihre Postleitzahl an.";
    if (!form.city.trim()) e.city = "Bitte geben Sie Ihren Ort an.";
    if (!form.service) e.service = "Bitte wählen Sie eine Leistung aus.";
    if (!form.message.trim()) e.message = "Bitte beschreiben Sie kurz Ihr Vorhaben.";
    if (!form.consent) e.consent = "Bitte stimmen Sie der Datenschutzerklärung zu.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onFiles = async (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setUploading(true);
    for (const file of arr) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await axios.post(`${API}/upload`, fd);
        setFiles((prev) => [...prev, { id: data.id, filename: data.filename }]);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Datei konnte nicht hochgeladen werden.");
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Bitte prüfen Sie die markierten Felder.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, { ...form, file_ids: files.map((f) => f.id) });
      setSuccess(true);
      setForm(emptyForm);
      setFiles([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ihre Anfrage konnte nicht gesendet werden.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="py-20 lg:py-28 bg-white scroll-mt-20" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
        {/* Left: intro + direct contact */}
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark mb-4">Kontakt</p>
            <h2 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-[1.15]">
              Beschreiben Sie uns Ihr Vorhaben
            </h2>
            <p className="mt-5 text-lg text-charcoal-muted leading-relaxed">
              Senden Sie uns die wichtigsten Informationen. Wir prüfen Ihre Anfrage und melden uns
              zur Abstimmung der nächsten Schritte.
            </p>

            <ul className="mt-8 space-y-3.5">
              <li>
                <a href={contact.phoneHref || undefined} data-testid="contact-phone" className={`flex items-center gap-3.5 group ${contact.phoneHref ? "" : "pointer-events-none"}`}>
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-forest/8 text-forest"><Phone className="w-5 h-5" /></span>
                  <span><span className="block text-sm text-charcoal-muted">Telefon</span><span className="font-medium text-charcoal group-hover:text-forest transition-colors">{contact.phone}</span></span>
                </a>
              </li>
              <li>
                <a href={contact.email.includes("@") ? `mailto:${contact.email}` : undefined} data-testid="contact-email" className="flex items-center gap-3.5 group">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-forest/8 text-forest"><Mail className="w-5 h-5" /></span>
                  <span><span className="block text-sm text-charcoal-muted">E-Mail</span><span className="font-medium text-charcoal group-hover:text-forest transition-colors break-all">{contact.email}</span></span>
                </a>
              </li>
              {contact.whatsapp && (
                <li>
                  <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-3.5 group">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-forest/8 text-forest"><MessageCircle className="w-5 h-5" /></span>
                    <span><span className="block text-sm text-charcoal-muted">WhatsApp</span><span className="font-medium text-charcoal group-hover:text-forest transition-colors">Nachricht senden</span></span>
                  </a>
                </li>
              )}
              <li className="flex items-center gap-3.5">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-forest/8 text-forest"><MapPin className="w-5 h-5" /></span>
                <span><span className="block text-sm text-charcoal-muted">Geschäftsadresse</span><span className="font-medium text-charcoal">{contact.address}</span><span className="block text-sm text-charcoal-muted">Einsatzgebiet: Bremen und Umgebung</span></span>
              </li>
            </ul>
          </Reveal>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-border bg-sand p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              {success ? (
                <div data-testid="form-success" className="text-center py-12 px-4">
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-forest/10 text-forest mb-5"><CheckCircle2 className="w-8 h-8" /></span>
                  <h3 className="font-heading font-bold text-2xl text-charcoal">Vielen Dank für Ihre Anfrage</h3>
                  <p className="mt-3 text-charcoal-muted leading-relaxed max-w-md mx-auto">
                    Ihre Angaben wurden erfolgreich übermittelt. Wir prüfen die Informationen und
                    melden uns zur weiteren Abstimmung.
                  </p>
                  <button onClick={() => setSuccess(false)} data-testid="form-reset" className="mt-6 inline-flex items-center rounded-md border border-forest px-5 py-2.5 text-forest font-semibold hover:bg-forest hover:text-white transition-colors">
                    Weitere Anfrage senden
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} noValidate data-testid="contact-form" className="space-y-5">
                  {/* honeypot */}
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Vor- und Nachname" required id="f-name" error={errors.name}>
                      <input id="f-name" data-testid="input-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} aria-invalid={!!errors.name} aria-describedby={errors.name ? "f-name-error" : undefined} />
                    </Field>
                    <Field label="Unternehmen" id="f-company">
                      <input id="f-company" data-testid="input-company" className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} />
                    </Field>
                    <Field label="Telefonnummer" required id="f-phone" error={errors.phone}>
                      <input id="f-phone" type="tel" data-testid="input-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "f-phone-error" : undefined} />
                    </Field>
                    <Field label="E-Mail-Adresse" required id="f-email" error={errors.email}>
                      <input id="f-email" type="email" data-testid="input-email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} aria-invalid={!!errors.email} aria-describedby={errors.email ? "f-email-error" : undefined} />
                    </Field>
                    <Field label="Postleitzahl" required id="f-plz" error={errors.postal_code}>
                      <input id="f-plz" data-testid="input-postal" className={inputCls} value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} aria-invalid={!!errors.postal_code} aria-describedby={errors.postal_code ? "f-plz-error" : undefined} />
                    </Field>
                    <Field label="Ort" required id="f-city" error={errors.city}>
                      <input id="f-city" data-testid="input-city" className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} aria-invalid={!!errors.city} aria-describedby={errors.city ? "f-city-error" : undefined} />
                    </Field>
                    <Field label="Kundentyp" id="f-type">
                      <select id="f-type" data-testid="select-customer-type" className={inputCls} value={form.customer_type} onChange={(e) => set("customer_type", e.target.value)}>
                        {customerTypes.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </Field>
                    <Field label="Gewünschte Leistung" required id="f-service" error={errors.service}>
                      <select id="f-service" data-testid="select-service" className={inputCls} value={form.service} onChange={(e) => set("service", e.target.value)} aria-invalid={!!errors.service} aria-describedby={errors.service ? "f-service-error" : undefined}>
                        <option value="" disabled>Bitte wählen …</option>
                        {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Art des Objekts oder Projekts" id="f-object">
                      <input id="f-object" data-testid="input-object" className={inputCls} value={form.object_type} onChange={(e) => set("object_type", e.target.value)} />
                    </Field>
                    <Field label="Ungefähre Fläche oder Projektgröße" id="f-size">
                      <input id="f-size" data-testid="input-size" className={inputCls} value={form.project_size} onChange={(e) => set("project_size", e.target.value)} />
                    </Field>
                  </div>

                  <Field label="Gewünschter Beginn" id="f-start">
                    <input id="f-start" data-testid="input-start" className={inputCls} placeholder="z. B. baldmöglichst, konkretes Datum oder flexibel" value={form.preferred_start} onChange={(e) => set("preferred_start", e.target.value)} />
                  </Field>

                  <Field label="Nachricht" required id="f-message" error={errors.message}>
                    <textarea id="f-message" data-testid="input-message" rows={4} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} aria-invalid={!!errors.message} aria-describedby={errors.message ? "f-message-error" : undefined} />
                  </Field>

                  {/* Upload */}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-charcoal">Bilder oder Dokumente (optional)</span>
                    <button type="button" onClick={() => fileRef.current?.click()} data-testid="upload-trigger" className="flex items-center justify-center gap-2 rounded-md border border-dashed border-input bg-white px-4 py-4 text-charcoal-muted hover:border-forest hover:text-forest transition-colors">
                      {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                      {uploading ? "Wird hochgeladen …" : "Dateien auswählen (Bilder, PDF, Word · max. 10 MB)"}
                    </button>
                    <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.gif,.heic,.pdf,.doc,.docx" className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="input-file" />
                    {files.length > 0 && (
                      <ul className="mt-1 space-y-1.5">
                        {files.map((f) => (
                          <li key={f.id} className="flex items-center justify-between gap-2 text-sm bg-white border border-border rounded-md px-3 py-2">
                            <span className="truncate text-charcoal">{f.filename}</span>
                            <button type="button" aria-label={`${f.filename} entfernen`} onClick={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))} className="text-charcoal-muted hover:text-destructive"><X className="w-4 h-4" /></button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Consent */}
                  <div className="flex flex-col gap-1.5">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" data-testid="input-consent" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 w-4 h-4 accent-forest" aria-invalid={!!errors.consent} />
                      <span className="text-sm text-charcoal-muted leading-relaxed">
                        Ich habe die <a href="/datenschutz" target="_blank" className="text-forest underline hover:text-forest-dark">Datenschutzerklärung</a> gelesen und stimme der Verarbeitung meiner Angaben zur Bearbeitung meiner Anfrage zu. <span className="text-forest">*</span>
                      </span>
                    </label>
                    {errors.consent && <p className="text-sm text-destructive" role="alert">{errors.consent}</p>}
                  </div>

                  <button type="submit" disabled={submitting} data-testid="submit-button" className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-forest px-6 py-3.5 text-white font-semibold hover:bg-forest-dark disabled:opacity-70 transition-colors">
                    {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Wird gesendet …</> : "Kostenloses Angebot anfordern"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
